import { Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn } from '@angular/forms';
import { Observable, of, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { GroupProxy, InvoiceProxy, PracticeProxy, UsersProxy } from 'src/app/shared/proxies';

@Injectable()
export class ValidationFactories {
    static debounceTime: number = 500;

    constructor(
        private practiceProxy: PracticeProxy,
        private usersProxy: UsersProxy,
        private invoiceProxy: InvoiceProxy,
        private groupProxy: GroupProxy,
    ) {}

    uniqueUser(): AsyncValidatorFn {
        return (
            control: AbstractControl
        ): Observable<{
            [key: string]: any;
        } | null> => {
            return timer(ValidationFactories.debounceTime).pipe(
                switchMap(async () => {
                    if (!control.value) {
                        return of(null);
                    }
                    const userExists = await this.usersProxy.userExists(control.value);
                    if (userExists) {
                        return {
                            userExists: {
                                message: $localize`:@@Validation_UserAlreadyExists:user already exists`,
                            },
                        };
                    }
                    return null;
                })
            );
        };
    }

    uniquePracticeName(): AsyncValidatorFn {
        return (
            control: AbstractControl
        ): Observable<{
            [key: string]: any;
        } | null> => {
            return timer(ValidationFactories.debounceTime).pipe(
                switchMap(async () => {
                    if (!control.value) {
                        return of(null);
                    }
                    const practiceName = control.value;
                    const practiceExists = await this.practiceProxy.practiceNameExists(practiceName);
                    if (practiceExists) {
                        return {
                            practiceNameExists: {
                                message: $localize`:@@Validation_PracticeAlreadyExists:practice already exists`,
                            },
                        };
                    }
                    return null;
                })
            );
        };
    }

    uniqueDebtorNumber(): AsyncValidatorFn {
        return (
            control: AbstractControl
        ): Observable<{
            [key: string]: any;
        } | null> => {
            return timer(ValidationFactories.debounceTime).pipe(
                switchMap(async () => {
                    if (!control.value) {
                        return of(null);
                    }
                    const debtorNumber = control.value;
                    const debtorNumberExists = await this.practiceProxy.debtorNumberExists(debtorNumber);
                    if (debtorNumberExists) {
                        return {
                            debtorNumberExists: {
                                message: $localize`:@@Validation_DebtorNumberAlreadyExists:debtor number already exists`,
                            },
                        };
                    }
                    return null;
                })
            );
        };
    }

    uniqueInvoiceNumber(): AsyncValidatorFn {
        return (
            control: AbstractControl
        ): Observable<{
            [key: string]: any;
        } | null> => {
            return timer(ValidationFactories.debounceTime).pipe(
                switchMap(async () => {
                    if (!control.value) {
                        return of(null);
                    }
                    const invoiceNumber = control.value;
                    const invoiceNumberExists = await this.invoiceProxy.invoiceNumberExists(invoiceNumber);
                    if (invoiceNumberExists) {
                        return {
                            invoiceNumberExists: {
                                message: $localize`:@@Validation_InvoiceNumberAlreadyExists:invoice number already exists`,
                            },
                        };
                    }
                    return null;
                })
            );
        };
    }

    uniqueGroupName(): AsyncValidatorFn {
        return (
            control: AbstractControl
        ): Observable<{
            [key: string]: any;
        } | null> => {
            return timer(ValidationFactories.debounceTime).pipe(
                switchMap(async () => {
                    if (!control.value) {
                        return of(null);
                    }
                    const groupNameExists = await this.groupProxy.groupNameExists(control.value);
                    if (groupNameExists) {
                        return {
                            groupNameExists: {
                                message: $localize`:@@Validation_GroupAlreadyExists:group already exists`,
                            },
                        };
                    }
                    return null;
                })
            );
        };
    }
}
